import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { BranchStep } from '../../../../../../dorian-shared/types/branch/BranchStep';
import { Video, VideoSettings } from '../../../../../../dorian-shared/types/video/video';
import { api } from '../../../../../api';
import { VideoPreviewButton } from './VideoPreviewButton';
import { VideoSettingsForm } from './VideoSettingsForm';
import { VideoTrackPicker } from './VideoTrackPicker';

interface StepsTypeVideoProps {
  stepIndex: number;
  value?: BranchStep['video'];
  handleChangeSteps: (
    value: { id: number; title?: string; url?: string; videoSettings?: VideoSettings } | undefined,
    stepIndex: number,
    key: string,
  ) => void;
  isDisabled: boolean;
}

function previewFromStep(value?: BranchStep['video'], hydrated?: Video | null): Video | null {
  if (!value?.id) {
    return null;
  }
  const title = value.title || hydrated?.title || 'Video';
  const videoUrl = value.url || hydrated?.videoUrl || '';
  if (!videoUrl) {
    return null;
  }
  return {
    id: value.id,
    title,
    alias: title,
    videoName: title,
    videoUrl,
    subtitleUrl: hydrated?.subtitleUrl || '',
  };
}

export function StepsTypeVideo({
  value,
  handleChangeSteps,
  stepIndex,
  isDisabled,
}: StepsTypeVideoProps): React.ReactElement {
  const [hydrated, setHydrated] = useState<Video | null>(null);

  const videoSettings = useMemo<VideoSettings>(() => (value?.videoSettings as VideoSettings) || {
    autoplay: true,
    allowStepActions: false,
  }, [value?.videoSettings]);

  useEffect(() => {
    if (!value?.id || (value.title && value.url)) {
      setHydrated(null);
      return undefined;
    }
    let cancelled = false;
    api.get(`/v1/video/${value.id}`)
      .then((res) => {
        if (!cancelled) {
          setHydrated(res.data.video || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHydrated(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [value?.id, value?.title, value?.url]);

  const selectedVideo = previewFromStep(value, hydrated);

  const handleSelect = useCallback((video: Video) => {
    handleChangeSteps(
      {
        id: video.id,
        title: video.title,
        url: video.videoUrl,
        videoSettings,
      },
      stepIndex,
      'video',
    );
  }, [handleChangeSteps, stepIndex, videoSettings]);

  const handleSettingsChange = useCallback((updatedSettings: VideoSettings) => {
    if (!value?.id) {
      return;
    }
    handleChangeSteps(
      {
        id: value.id,
        title: value.title,
        url: value.url,
        videoSettings: updatedSettings,
      },
      stepIndex,
      'video',
    );
  }, [handleChangeSteps, stepIndex, value]);

  return (
    <Col>
      <Row>
        <Col sm={6} className="d-flex align-items-center">
          <VideoTrackPicker
            selectedId={value?.id}
            selectedTitle={value?.title || hydrated?.title}
            disabled={isDisabled}
            onSelect={handleSelect}
          />
          <input type="hidden" name={`steps[${stepIndex}][video][id]`} value={value?.id || ''} />
        </Col>
        {selectedVideo && (
          <>
            <VideoPreviewButton
              video={selectedVideo}
              videoSettings={videoSettings}
              isDisabled={isDisabled}
            />
            <VideoSettingsForm
              videoSettings={videoSettings}
              stepIndex={stepIndex}
              isDisabled={isDisabled}
              onChange={handleSettingsChange}
            />
          </>
        )}
      </Row>
    </Col>
  );
}
